/**
 * Browser-side companion to snippets/localized-money.liquid.
 * Prices passed here are Shopify's market-aware integer values in cents.
 * This code formats those values only; Shopify remains responsible for
 * selecting the market, currency, converted amount, and checkout price.
 */
(() => {
  const prefixes = {
    USD: '$',
    CAD: 'C$',
    AUD: 'A$',
    NZD: 'NZ$',
    SGD: 'S$',
    HKD: 'HK$',
    TWD: 'NT$',
    MXN: 'MX$',
  };

  const defaultCurrency = (document.documentElement.dataset.currency || 'USD').toUpperCase();
  const sampleCents = 123456789;

  /**
   * Learn Shopify's current market punctuation from a server-rendered sample.
   * This matters when the language and market differ: Shopify can render
   * French-market EUR as €35,95 while the document language remains English.
   */
  const patternFrom = (sample) => {
    const firstDigit = sample.search(/\d/);
    const lastDigit = Math.max(...[...sample].map((char, index) => (/\d/.test(char) ? index : -1)));
    if (firstDigit < 0 || lastDigit < firstDigit) return null;

    const body = sample.slice(firstDigit, lastDigit + 1);
    const digits = body.replace(/\D/g, '');
    let decimals = 2;
    for (let candidate = 0; candidate <= 3; candidate += 1) {
      const scaled = Math.round((sampleCents / 100) * (10 ** candidate)).toString();
      if (scaled === digits) {
        decimals = candidate;
        break;
      }
    }

    const separators = body.match(/[^\d]+/g) || [];
    const decimalSeparator = decimals > 0 ? (separators[separators.length - 1] || '.') : '';
    const groupingSeparators = decimals > 0 ? separators.slice(0, -1) : separators;

    return {
      prefix: sample.slice(0, firstDigit),
      suffix: sample.slice(lastDigit + 1),
      decimals,
      decimalSeparator,
      groupingSeparator: groupingSeparators[0] || '',
    };
  };

  const moneyPattern = patternFrom(document.documentElement.dataset.moneySample || '');
  const numberPattern = patternFrom(document.documentElement.dataset.numberSample || '');

  const applyPattern = (cents, pattern, prefixOverride) => {
    if (!pattern) return null;
    const factor = 10 ** pattern.decimals;
    const scaled = Math.round((Number(cents) / 100) * factor);
    const padded = String(Math.abs(scaled)).padStart(pattern.decimals + 1, '0');
    const integerLength = pattern.decimals ? padded.length - pattern.decimals : padded.length;
    let integer = padded.slice(0, integerLength);
    const fraction = pattern.decimals ? padded.slice(integerLength) : '';

    if (pattern.groupingSeparator) {
      integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, pattern.groupingSeparator);
    }

    const sign = scaled < 0 ? '-' : '';
    const number = sign + integer + (fraction ? pattern.decimalSeparator + fraction : '');
    return (prefixOverride === undefined ? pattern.prefix : prefixOverride) + number +
      (prefixOverride === undefined ? pattern.suffix : '');
  };

  const format = (cents, currencyCode = defaultCurrency) => {
    const currency = String(currencyCode || defaultCurrency).toUpperCase();
    if (currency === defaultCurrency) {
      const patterned = prefixes[currency]
        ? applyPattern(cents, numberPattern, prefixes[currency])
        : applyPattern(cents, moneyPattern);
      if (patterned !== null) return patterned;
    }

    const value = Number(cents) / 100;
    const locale = document.documentElement.lang || navigator.language || 'en';

    try {
      const currencyFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      });

      if (!prefixes[currency]) return currencyFormatter.format(value);

      const resolved = currencyFormatter.resolvedOptions();
      const number = new Intl.NumberFormat(locale, {
        minimumFractionDigits: resolved.minimumFractionDigits,
        maximumFractionDigits: resolved.maximumFractionDigits,
      }).format(value);
      return prefixes[currency] + number;
    } catch (error) {
      return (prefixes[currency] || '') + value.toFixed(2);
    }
  };

  window.MSPMoney = { format, currency: defaultCurrency };
})();
